// =============================================================================
// src/engine/pitch.ts
// Utilities for converting between note names and MIDI numbers.
// =============================================================================

// Note name to semitone offset from C
const NOTE_TO_SEMITONE: Record<string, number> = {
  'C': 0, 'D': 2, 'E': 4, 'F': 5, 'G': 7, 'A': 9, 'B': 11
};

// Semitone to note name (sharp preference)
const SEMITONE_TO_NOTE_SHARP = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Semitone to note name (flat preference)
const SEMITONE_TO_NOTE_FLAT = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

/**
 * Parse a note name (e.g., "C#4", "Bb3", "E5") to MIDI number.
 * MIDI 60 = C4 (middle C).
 * 
 * @param note Note name with optional accidental and octave
 * @returns MIDI number
 * @throws Error if note name is invalid
 */
export function parseNoteToMidi(note: string): number {
  const match = note.match(/^([A-Ga-g])([#b]?)(-?\d+)?$/);
  if (!match) {
    throw new Error(`Invalid note name: "${note}"`);
  }

  const [, letter, accidental, octaveStr] = match;
  const baseSemitone = NOTE_TO_SEMITONE[letter.toUpperCase()];
  
  if (baseSemitone === undefined) {
    throw new Error(`Unknown note letter: "${letter}"`);
  }

  let semitone = baseSemitone;
  if (accidental === '#') semitone += 1;
  if (accidental === 'b') semitone -= 1;

  // Default octave is 4 if not specified
  const octave = octaveStr !== undefined ? parseInt(octaveStr, 10) : 4;

  // MIDI: C4 = 60, so C0 = 12
  return 12 + (octave * 12) + semitone;
}

/**
 * Convert MIDI number to note name.
 * 
 * @param midi MIDI number
 * @param preferSharps Use sharps instead of flats for accidentals
 * @returns Note name with octave (e.g., "C#4")
 */
export function midiToNoteName(midi: number, preferSharps = true): string {
  const semitone = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const noteNames = preferSharps ? SEMITONE_TO_NOTE_SHARP : SEMITONE_TO_NOTE_FLAT;
  return `${noteNames[semitone]}${octave}`;
}

/**
 * Convert MIDI number to pitch class (note name without octave).
 * 
 * @param pitchClass Pitch class (0-11, 0=C)
 * @param preferSharps Use sharps instead of flats
 * @returns Note name without octave (e.g., "C#")
 */
export function midiToNoteClass(pitchClass: number, preferSharps = true): string {
  const pc = ((pitchClass % 12) + 12) % 12;
  const noteNames = preferSharps ? SEMITONE_TO_NOTE_SHARP : SEMITONE_TO_NOTE_FLAT;
  return noteNames[pc];
}

/**
 * Calculate interval in semitones between two pitch classes (mod 12).
 * Result is always in range [0, 11].
 * 
 * @param fromPC Starting pitch class
 * @param toPC Target pitch class
 * @returns Interval in semitones (0-11)
 */
export function intervalMod12(fromPC: number, toPC: number): number {
  return ((toPC - fromPC) % 12 + 12) % 12;
}
